import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { DEFAULT_FEE_ATTACHMENT_BODY } from 'citadel-shared/variables.js'

import axios from '../../dataClient/dataClient'
import { replaceConfigVariables } from '../../util/variableFormatting'
import { attachmentStatuses } from '../../util/attachmentConsts'
import { communicationSchema } from '../../schemas/communicationSchema'
import { ErrorMessage } from '../common/UserMessage'
import {
  FlexColumn,
  FlexEndButtonErrorContainer,
  FlexEndContainer,
  FlexRow,
  Form,
  OutlineContainer,
  SpaceBetweenRowContainer
} from '../styles/Containers'
import { BoldMedBodyText, MedBodyText, Subheading } from '../styles/Text'
import Button, { SecondaryButton } from '../common/Button'
import RichTextReadOnly from '../inputFields/RichTextReadOnly'
import SelectLetterForm from '../letters/SelectLetterForm'
import SelectBillForm from '../bill/SelectBillForm'
import CommunicationForm from './CommunicationForm'

const UPDATE_COMMUNICATION_ERROR = 'Error updating communication. Please try again.'

const LastModifiedRow = styled(FlexRow)`
  flex-wrap: wrap;
`

function formValuesFromCommunication(communication) {
  return {
    name: communication?.name ?? '',
    body: communication?.body ?? '',
    billId: communication?.bill?.id ?? '',
    letterId: communication?.letter?.id ?? ''
  }
}

function formatDate(date) {
  if (!date) return '—'
  return format(date, 'MMM-dd-yyyy hh:mm a')
}

function CommunicationEditable({
  communication,
  publicConfig = {},
  letters = [],
  bills = [],
  handleUpdateCommunication = () => {},
  handleCancelUpdateCommunication = () => {}
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const [baseline, setBaseline] = useState(formValuesFromCommunication(communication))

  const formMethods = useForm({
    resolver: yupResolver(communicationSchema),
    defaultValues: formValuesFromCommunication(communication)
  })

  const { reset, handleSubmit } = formMethods

  const onError = (errors, e) => console.error('Error', errors, e)

  function handleCancel() {
    reset({ ...baseline })
    setError(false)
    handleCancelUpdateCommunication()
  }

  function onSubmit(values) {
    setError(false)
    setLoading(true)

    const payload = {
      ...values,
      billId: values.billId !== '' ? values.billId : null,
      letterId: values.letterId !== '' ? values.letterId : null
    }

    axios
      .patch(`/communication/${communication.id}`, payload)
      .then(({ data }) => {
        const saved = data.data
        const nextValues = formValuesFromCommunication(saved)
        const nextUpdatedAt = saved.updatedAt ?? baseline.updatedAt

        setBaseline({ ...nextValues, updatedAt: nextUpdatedAt })
        reset({ ...nextValues })
        handleUpdateCommunication(saved)
      })
      .catch(e => {
        console.error('Failed to update the communication', e)
        const serverMessage = [400, 409].includes(e.response?.status) ? e.response?.data?.message : null
        setError(serverMessage || UPDATE_COMMUNICATION_ERROR)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const normalizedFeeAttachmentBody = replaceConfigVariables({ html: DEFAULT_FEE_ATTACHMENT_BODY, publicConfig })

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit, onError)} gap="10px">
        <FlexEndButtonErrorContainer gap="5px">
          <FlexEndContainer gap="5px">
            <SecondaryButton onClick={handleCancel} disabled={loading}>
              Cancel
            </SecondaryButton>
            <Button type="submit" loading={loading}>
              Save
            </Button>
          </FlexEndContainer>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </FlexEndButtonErrorContainer>
        <OutlineContainer padding="25px 30px">
          <FlexColumn>
            <SpaceBetweenRowContainer gap="10px">
              <Subheading>Draft</Subheading>
              <LastModifiedRow gap="5px">
                <MedBodyText>Last modified:</MedBodyText>
                <BoldMedBodyText>{formatDate(communication.updatedAt)}</BoldMedBodyText>
              </LastModifiedRow>
            </SpaceBetweenRowContainer>
            <CommunicationForm disabled={loading} />
            <FlexColumn gap="10px">
              <Subheading>Attachments</Subheading>
              <SelectLetterForm foiId={communication.foiId} letters={letters} letterLabel="Letter:" disabled={loading} />
              <SelectBillForm foiId={communication.foiId} bills={bills} label="Fee:" disabled={loading} />
            </FlexColumn>
            <FlexColumn gap="10px">
              <MedBodyText>The following text will be automatically included in your communication when a fee is attached:</MedBodyText>
              <RichTextReadOnly value={normalizedFeeAttachmentBody} />
            </FlexColumn>
          </FlexColumn>
        </OutlineContainer>
      </Form>
    </FormProvider>
  )
}

CommunicationEditable.propTypes = {
  communication: PropTypes.shape({
    id: PropTypes.string.isRequired,
    foiId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    updatedAt: PropTypes.instanceOf(Date).isRequired
  }).isRequired,
  publicConfig: PropTypes.object,
  letters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.oneOf(attachmentStatuses)
    })
  ),
  bills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.oneOf(attachmentStatuses)
    })
  ),
  handleUpdateCommunication: PropTypes.func,
  handleCancelUpdateCommunication: PropTypes.func
}

export default CommunicationEditable
